import React, { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { isNil } from 'lodash'
import PropTypes from 'prop-types'

import { showErrorNotification } from '../../../core/helpers'
import { areSameValue } from '../../../core/utils'

import { EditableTableRowLoader } from '../../../common/components/sections'
import { StyledHighlightedTableRow } from '../../../common/components/styled'
import {
  CURATION_CERTAINTY_THRESHOLD,
  TABLE_ROW_HIGHLIGHT_STYLES,
} from '../../../common/constants'

import { TIER_FILTER } from '../../constants'
import { useUpdateDishMutation } from '../../hooks'
import { selectDishById } from '../../redux/selectors'

const DishEditableRow = ({ ...rowProps }) => {
  const rowRef = useRef(null)
  const { 'data-row-key': id, children, className, ...restProps } = rowProps
  const [, { isLoading, error }] = useUpdateDishMutation(id)

  const selectedDishId = useSelector(
    store => store.dishes.dishes?.drawer?.selectedDishId,
  )

  const dish = useSelector(state => selectDishById(state, id))

  const { rowHighlight, isCellHighlightDisabled } = useMemo(() => {
    if (dish?.isLocked) {
      return {
        rowHighlight: TABLE_ROW_HIGHLIGHT_STYLES.LOCKED,
        isCellHighlightDisabled: true,
      }
    }

    if (dish?.isDirty) {
      return { rowHighlight: TABLE_ROW_HIGHLIGHT_STYLES.REQUIRES_ATTENTION }
    }

    if (areSameValue(dish?.tier, TIER_FILTER.ONE.value)) {
      return { rowHighlight: TABLE_ROW_HIGHLIGHT_STYLES.LIKELY_TO_BE_WRONG }
    }

    if (areSameValue(dish?.tier, TIER_FILTER.TWO.value)) {
      return { rowHighlight: TABLE_ROW_HIGHLIGHT_STYLES.ERROR }
    }

    if (dish?.isManualIgnored || dish?.isAiIgnored) {
      return {
        rowHighlight: TABLE_ROW_HIGHLIGHT_STYLES.IGNORED,
        isCellHighlightDisabled: true,
      }
    }

    if (dish?.certainty < CURATION_CERTAINTY_THRESHOLD) {
      return { rowHighlight: TABLE_ROW_HIGHLIGHT_STYLES.LOW_CERTAINTY }
    }

    return []
  }, [dish])

  const isRowSelected = useMemo(
    () => areSameValue(selectedDishId, id),
    [id, selectedDishId],
  )

  useEffect(() => {
    if (!isNil(error)) {
      showErrorNotification({
        title: 'Dish update failed',
        description: error,
      })
    }
  }, [error])

  return (
    <>
      <EditableTableRowLoader
        isLoading={isLoading}
        height={rowRef?.current?.offsetHeight}
      />
      <StyledHighlightedTableRow
        data-row-key={id}
        ref={rowRef}
        highlight={rowHighlight}
        isCellHighlightDisabled={isCellHighlightDisabled}
        isSelected={isRowSelected}
        className={className}
        {...restProps}
      >
        {children}
      </StyledHighlightedTableRow>
    </>
  )
}

DishEditableRow.propTypes = {
  rowProps: PropTypes.object,
}

DishEditableRow.defaultProps = {
  rowProps: undefined,
}

export default DishEditableRow
